import { CancellationToken } from 'infra/cancellation'
import { orderBy } from 'lodash'
import memoize from 'memoizee'
import { VideoCall, VideoCallStatus } from 'messaging/calling/VideoCall'
import {
  VideoCallParticipant,
  VideoCallParticipantStatus,
} from 'messaging/calling/VideoCallParticipant'
import CallParticipantViewModel from 'messaging/calling/CallParticipantViewModel'
import {
  CallDescriptor,
  CallPorts,
  CallSounds,
  VideoCallSource,
} from 'messaging/calling/ports'
import { VideoCallConnectionDescriptorDto } from 'messaging/dtos/video-calls'
import { action, computed, observable, makeObservable } from 'mobx'
import { task } from 'mobx-task'
import { asapScheduler, merge, NEVER, Subject } from 'rxjs'
import {
  filter,
  observeOn,
  skip,
  switchMap,
  take,
  takeUntil,
} from 'rxjs/operators'
import { asAtom, toStream } from 'utils/mobx-util'
import { cancelWhen, fromClock, shareLatest } from 'utils/rx-util'
import CallMediaSettingsModalViewModel from './CallMediaSettingsModalViewModel'

export const enum CallOperatorState {
  /**
   * Call is inactive and we are not connected.
   */
  Inactive = 'Inactive',
  /**
   * Call is incoming, waiting for a response. There can be multiple incoming
   * calls at once, although that is highly unlikely.
   */
  Incoming = 'Incoming',
  /**
   * Call is being joined. Set to this before connecting.
   */
  Connecting = 'Connecting',
  /**
   * Call is currently active. When a call is active, it must be hung up
   * before an incoming call can be accepted.
   */
  Connected = 'Connected',
}

/**
 * Call view mode.
 */
export const enum ViewMode {
  /**
   * The actual call itself is hidden, may be rendered as prompt.
   */
  Hidden,
  /**
   * The call details are being rendered somewhere.
   */
  Visible,
}

/**
 * Delegates needed to interact with the call backend.
 */
export interface CallViewModelDelegates {
  connect(descriptor: CallDescriptor): Promise<void>
}

/**
 * Call view model.
 */
export default class CallViewModel {
  /**
   * Operator state.
   */
  @observable state = CallOperatorState.Inactive

  /**
   * The call view mode.
   */
  @observable viewMode = ViewMode.Hidden

  /**
   * The call source, if any.
   */
  @observable callSource: VideoCallSource | null = null

  /**
   * Whether the ActiveCall component is rendered.
   */
  @observable activeCallRendered = false

  /**
   * Memoized mapper for participants.
   *
   * @private
   */
  readonly participantViewModelFor = memoize(
    (participant: VideoCallParticipant) =>
      new CallParticipantViewModel(this, participant)
  )

  /**
   * Dispose backing subject.
   * @private
   */
  private readonly _disposed = new Subject()

  /**
   * Emits when the VN is disposed.
   * Used as a stream terminator.
   */
  readonly disposed$ = this._disposed.pipe(
    shareLatest(),
    take(1),
    // Schedule emissions on the microtask queue.
    // Otherwise, if a subscriber calls `dispose`
    // from a subscription of a stream that uses
    // takeUntil(disposed$), the other subscribers will
    // not get notified.
    observeOn(asapScheduler)
  )

  /**
   * State as a stream.
   */
  readonly state$ = toStream(() => this.state, true).pipe(
    takeUntil(this.disposed$)
  )

  /**
   * Like `state$` but only emits when it changes.
   */
  readonly stateChange$ = this.state$.pipe(skip(1))

  /**
   * Streams as observables/atoms.
   *
   * @private
   */
  private readonly $ = {
    // Using asAtom so we unsub from the clock observable when no longer needed.
    // This ensures that any computed will recompute when the clock emits every
    // second.
    now: asAtom(
      fromClock(this.adapter.getNow).pipe(takeUntil(this.disposed$)),
      this.adapter.getNow()
    ),
    selectedMediaDevices: asAtom(this.adapter.selectedDevices$, []),
  }

  /**
   * Emits when the call is completed
   */
  readonly completed$ = this.call.completed$.pipe(takeUntil(this.disposed$))

  callMediaSettingsModalViewModel = new CallMediaSettingsModalViewModel(
    this.adapter
  )

  /**
   * Constructor.
   */
  constructor(
    public call: VideoCall,
    private adapter: CallPorts,
    private sounds: CallSounds,
    private delegates: CallViewModelDelegates
  ) {
    makeObservable(this)
    // If we are not in Inactive state, when the call is completed, try to disconnect.
    this.state$
      .pipe(
        switchMap((state) =>
          state === CallOperatorState.Inactive ? NEVER : this.completed$
        ),
        takeUntil(this.disposed$)
      )
      .subscribe(this.tryDisconnect)

    this.adapter.hangupSignal$
      .pipe(
        filter(
          () =>
            this.state === CallOperatorState.Connecting ||
            this.state === CallOperatorState.Connected
        )
      )
      .subscribe(this.hangup)

    this.adapter.mediaDeviceChanged$
      .pipe(takeUntil(this.disposed$))
      .subscribe(this.switchDevice)
  }

  /**
   * Participant view models.
   */
  @computed
  get participants() {
    return this.call.participants.map(this.participantViewModelFor)
  }

  /**
   * Participant view model for the current user participant, if any.
   */
  @computed
  get currentUserParticipant() {
    return this.call.currentUserParticipant
      ? this.participantViewModelFor(this.call.currentUserParticipant)
      : null
  }

  /**
   * Participants that are connected, excluding the current user.
   */
  @computed
  get otherUserParticipants() {
    return this.call.participants
      .filter((p) => p !== this.call.currentUserParticipant)
      .map(this.participantViewModelFor)
  }

  @computed
  get connectedParticipants() {
    return this.otherUserParticipants.filter(
      (p) => p.participant.status === VideoCallParticipantStatus.Connected
    )
  }

  @computed
  get participantsWithAudio() {
    return this.connectedParticipants.filter((p) => !p.media?.muted)
  }

  @computed
  get participantsWithVideo() {
    return this.connectedParticipants.filter((p) => p.media?.camera)
  }

  /**
   * The VM for the participant that is the center of attention.
   *
   * From the connected participants (including the current user)
   * - If one participant => use self
   * - If two => use the other participant
   * - If 3+ =>
      - If there's people with audio => use the loudest speaker
   *  - If there's people with video => use the first person with video
      - If no audio and no video => the first participant
   *
   */
  @computed
  get centerOfAttention() {
    // Obama's elf
    if (
      this.call.currentUserParticipant &&
      this.connectedParticipants.length === 0
    ) {
      return this.participantViewModelFor(this.call.currentUserParticipant)
    }

    // 1 on 1 call
    if (this.connectedParticipants.length === 1) {
      return this.connectedParticipants[0]
    }

    // 3+ call
    // Use loudest
    if (this.participantsWithAudio.length > 0) {
      const byLoudness = orderBy(this.participantsWithAudio, [
        (p) => p.media?.getAudioLoudness(),
      ])

      return byLoudness[0]
    }

    // // Or whoever has video
    if (this.participantsWithVideo.length > 0) {
      return this.participantsWithVideo[0]
    }

    // Or just anyone
    return this.connectedParticipants[0]
  }

  /**
   * Duration in milliseconds.
   */
  @computed
  get duration() {
    const relative = this.call.dateCompleted ?? this.$.now()
    const absolute = relative.valueOf() - this.call.dateCreated.valueOf()
    // Round to the nearest second.
    return absolute - (absolute % 1000)
  }

  @computed
  get canEnableCamera() {
    return this.adapter.canHaveOutgoingVideo(this.call)
  }

  /**
   * Navigates the user to the call context (e.g. the Job
   * for the containing conversation)
   */
  @action.bound
  async revealCallContext() {
    this.adapter.showCallContext(this.call)
    this.show()
  }

  /**
   * Fetches the call info.
   */
  fetch = task(async () => {
    await this.adapter.fetch(this.call)
  })

  /**
   * Start the call.
   */
  start = task.resolved(async (descriptor: CallDescriptor) => {
    // Cancel the ringing sound when:
    //   * at least one other person is no longer Invited,
    //   * the operator state changes to Inactive
    //   * the call ends
    const cts = cancelWhen(
      merge(
        toStream(() =>
          this.call.otherParticipants.some(
            (p) => p.status !== VideoCallParticipantStatus.Invited
          )
        ).pipe(filter(Boolean)),
        this.completed$,
        this.stateChange$.pipe(filter((s) => s === CallOperatorState.Inactive))
      ).pipe(takeUntil(this.disposed$))
    )

    this.sounds.ringing.playLoop(cts.token)
    this.show()
    await this.connect(descriptor)
  })

  /**
   * Shows the call details.
   */
  @action.bound
  show() {
    this.viewMode = ViewMode.Visible
  }

  /**
   * Hides the call details.
   */
  @action.bound
  hide() {
    this.viewMode = ViewMode.Hidden
  }

  /**
   * ActiveCall component has been mounted.
   */
  @action.bound
  activeCallMounted() {
    this.activeCallRendered = true
  }

  /**
   * ActiveCall component has been unmounted.
   */
  @action.bound
  activeCallUnmounted() {
    this.activeCallRendered = false
  }

  /**
   * Join the call.
   */
  join = task.resolved(async () => {
    const descriptor = await this.adapter.join(this.call)
    this.revealCallContext()
    return this.connect(descriptor)
  })

  /**
   * Connects using the specified descriptor.
   * This will call the delegate which later calls `connectToSource`.
   */
  connect = task.resolved(async (descriptor: CallDescriptor) => {
    this.setOperatorState(CallOperatorState.Connecting)
    return this.delegates.connect(descriptor)
  })

  /**
   * Decline the call.
   */
  decline = task.resolved(async () => {
    this.call.maybeMarkCompleted()
    this.setOperatorState(CallOperatorState.Inactive)
    return this.adapter.decline(this.call)
  })

  /**
   * Hang up.
   */
  hangup = task.resolved(async () => {
    await this.tryDisconnect()
    this.setOperatorState(CallOperatorState.Inactive)
    return this.adapter.hangup(this.call)
  })

  /**
   * Connect to the call source. This is called by the
   * parent of this VM.
   */
  connectToSource = task.resolved(
    async (
      connectionDescriptor: VideoCallConnectionDescriptorDto,
      ct: CancellationToken
    ) => {
      const callSource = await this.adapter.acquireCallSource(
        this,
        connectionDescriptor,
        ct
      )
      this.setCallSource(callSource)
      await callSource.connect(ct)
      this.setOperatorState(CallOperatorState.Connected)
    }
  )

  /**
   * Disconnects from the call source.
   */
  disconnect = task.resolved(async () => {
    if (this.state === CallOperatorState.Inactive) {
      return
    }

    console.log('Disconnect!')
    await this.callSource?.disconnect()
    this.clearCallSource()
    this.setOperatorState(CallOperatorState.Inactive)
  })

  @computed
  get preferredCamera() {
    return (
      this.$.selectedMediaDevices().find((d) => d?.kind === 'videoinput') ??
      null
    )
  }

  @computed
  get preferredMicrophone() {
    return (
      this.$.selectedMediaDevices().find((d) => d?.kind === 'audioinput') ??
      null
    )
  }

  @action.bound
  async switchDevice(newDevice: MediaDeviceInfo) {
    // So we are double toggling in order to setup the tracks of
    // the new device while preserving the state of the camera/micro being
    // off/on.  Need to find a more elegant solution but this is V1.
    if (newDevice.kind === 'videoinput') {
      await this.toggleCamera()
      await this.toggleCamera()
    }

    if (newDevice.kind === 'audioinput') {
      await this.toggleMicrophone()
      await this.toggleMicrophone()
    }
  }

  /**
   * Toggles the camera.
   */
  toggleCamera = task.resolved(async () => {
    const callSource = this.callSource
    const currentUserParticipantMedia = this.currentUserParticipant?.media
    if (!currentUserParticipantMedia || !callSource) {
      return
    }

    if (!currentUserParticipantMedia.camera) {
      await callSource.turnOnCamera(this.preferredCamera?.deviceId)
    } else {
      await callSource.turnOffCamera()
    }
  })

  /**
   * Toggles the microphone.
   */
  toggleMicrophone = task.resolved(async () => {
    const callSource = this.callSource
    const currentUserParticipantMedia = this.currentUserParticipant?.media
    if (!currentUserParticipantMedia || !callSource) {
      return
    }

    if (!currentUserParticipantMedia.muted) {
      await callSource.turnOffMicrophone()
    } else {
      await callSource.turnOnMicrophone(this.preferredMicrophone?.deviceId)
    }
  })

  /**
   * Called (no pun intended) when the call is being connected to.
   * Do not call from the UI.
   */
  @action.bound
  reportConnecting() {
    console.log('Connecting', this.call)
    this.setOperatorState(CallOperatorState.Connecting)
  }

  /**
   * Called (no pun intended) when the call is incoming.
   * Do not call from the UI.
   */
  @action.bound
  reportIncoming() {
    this.setOperatorState(CallOperatorState.Incoming)
  }

  /**
   * Called when the VM is disposed and not to be used again.
   */
  dispose() {
    this._disposed.next(undefined)
  }

  /**
   * Tries to disconnect. Called reactively when the call is ended.
   */
  private tryDisconnect = async () => {
    if (this.state === CallOperatorState.Connected) {
      return this.disconnect()
    }

    this.setOperatorState(CallOperatorState.Inactive)
  }

  /**
   * Sets the operator state.
   *
   * @private
   * @param state
   */
  @action.bound
  private setOperatorState(state: CallOperatorState) {
    if (this.call.callStatus === VideoCallStatus.Completed) {
      // We cannot set the operator state to something indicating an active call
      // when the call has ended.
      this.state = CallOperatorState.Inactive
    } else {
      this.state = state
    }
  }

  /**
   * Sets the call source.
   *
   * @param callSource
   * @private
   */
  @action.bound
  private setCallSource(callSource: VideoCallSource) {
    this.callSource = callSource
  }

  /**
   * Clears the call source.
   *
   * @private
   */
  @action.bound
  private clearCallSource() {
    if (!this.callSource) {
      return
    }
    this.callSource.dispose()
    this.callSource = null
  }
}
